import React, {useEffect, useId, useRef, useState} from 'react';
import {isKeyEvent} from '@/components/wyscout/MatchEvents/helpers';
import type {WyscoutPlayerFixtureDetail} from '@/features/players/wyscoutFixtureApi';
import EventTimeline from '@/pages/matches/detail/wyscout-events/EventTimeline';
import {playerMatchMomentItems, playerMatchMoments} from './playerMatchMoments';

interface WyscoutPlayerMatchContentProps {
  detail: WyscoutPlayerFixtureDetail;
  showHeading?: boolean;
  footerAction?: React.ReactNode;
  allEventsAnalysis?: React.ReactNode;
  onRetry?: () => void;
}

const available = (value: number | null, decimals = 0): string =>
  value === null ? 'Not available' : decimals ? value.toFixed(decimals) : String(value);

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }).format(date);
};

const unavailableVideoRequest = () => Promise.reject(new Error('Video is unavailable in this view.'));

const WyscoutPlayerMatchContent: React.FC<WyscoutPlayerMatchContentProps> = ({
  detail,
  showHeading = true,
  footerAction,
  allEventsAnalysis,
  onRetry,
}) => {
  const resolvedPlayerName = (playerId: number): string | null => detail.playerNames[playerId]?.trim() || null;
  const lineupPlayerLabel = (player: {playerId: number; shirtNumber?: string}): string =>
    resolvedPlayerName(player.playerId) || (player.shirtNumber ? `#${player.shirtNumber}` : 'Unknown player');
  const substitutionPlayerLabel = (playerId: number): string => resolvedPlayerName(playerId) || 'Unknown player';
  const classifiedMoments = playerMatchMoments(detail.events, detail.player.wyscoutId);
  const targetLineup = detail.lineups.find(team => [...team.lineup, ...team.bench].some(player => player.playerId === detail.player.wyscoutId));
  const normalizedTargetSubstitutions = detail.substitutionStatus === 'available'
    ? detail.substitutions.filter(substitution => substitution.playerInId === detail.player.wyscoutId || substitution.playerOutId === detail.player.wyscoutId)
    : [];
  const starter = (targetLineup?.lineup.some(player => player.playerId === detail.player.wyscoutId) ?? false)
    || normalizedTargetSubstitutions.some(substitution => substitution.playerOutId === detail.player.wyscoutId);
  const substitute = !starter && ((targetLineup?.bench.some(player => player.playerId === detail.player.wyscoutId) ?? false)
    || normalizedTargetSubstitutions.some(substitution => substitution.playerInId === detail.player.wyscoutId));
  const participation = starter
    ? 'Starter'
    : substitute ? 'Substitute' : detail.fixture.participationStatus === 'did_not_play' ? 'Did not play' : 'Played';
  const didNotPlay = participation === 'Did not play';
  const playerMomentItems = didNotPlay ? [] : playerMatchMomentItems(detail.events, normalizedTargetSubstitutions, detail.player.wyscoutId);
  const momentItems = playerMomentItems.filter(item => item.kind === 'substitution' || isKeyEvent(item.event));
  const moments = momentItems.filter((item): item is Extract<typeof item, {kind: 'event'}> => item.kind === 'event');
  const detailIdentity = `${detail.player.id}:${detail.player.wyscoutId}:${detail.match.matchId}`;
  const firstMomentId = moments[0]?.event.id ?? null;
  const [view, setView] = useState<'moments' | 'all'>('moments');
  const [selectedEventId, setSelectedEventId] = useState<number | null>(firstMomentId);
  const selectedMomentIsValid = moments.some(moment => moment.event.id === selectedEventId);
  const detailIdentityRef = useRef(detailIdentity);
  const momentsTabRef = useRef<HTMLButtonElement>(null);
  const allTabRef = useRef<HTMLButtonElement>(null);
  const momentsTabId = useId();
  const allTabId = useId();
  const panelId = useId();
  const emphasizedEventIds = new Set(classifiedMoments.map(moment => moment.event.id));
  const relationshipLabels = new Map(classifiedMoments.map(moment => [moment.event.id, moment.relationshipLabel] as const));
  const fixture = detail.fixture;
  const playerIsHome = fixture.playerTeamId === detail.match.homeTeamId;
  const opponentIsHome = fixture.opponentTeamId === detail.match.homeTeamId;
  const homeTeamName = playerIsHome
    ? fixture.playerTeamName
    : opponentIsHome ? fixture.opponentTeamName : null;
  const awayTeamName = playerIsHome
    ? fixture.opponentTeamName
    : opponentIsHome ? fixture.playerTeamName : null;
  const homeScore = playerIsHome ? fixture.playerScore : opponentIsHome ? fixture.opponentScore : null;
  const awayScore = playerIsHome ? fixture.opponentScore : opponentIsHome ? fixture.playerScore : null;
  const title = fixture.label || `${homeTeamName || 'Home team'} v ${awayTeamName || 'Away team'}`;
  const teamName = (teamId: number): string => teamId === detail.match.homeTeamId
    ? (homeTeamName || `Team ${teamId}`)
    : teamId === detail.match.awayTeamId ? (awayTeamName || `Team ${teamId}`) : `Team ${teamId}`;
  const timelineEvents = view === 'moments' ? moments.map(moment => moment.event) : detail.events;
  const targetSubstitutions = momentItems.filter((item): item is Extract<typeof item, {kind: 'substitution'}> => item.kind === 'substitution');
  let eventSlot = 0;
  const substitutionSlots = new Map<number, React.ReactNode>();
  for (const item of momentItems) {
    if (item.kind === 'event') {
      eventSlot += 1;
      continue;
    }
    const substitution = (
      <div key={`${item.minute}:${item.direction}:${item.counterpartPlayerId}`} data-player-moment className="mb-1 rounded-lg border border-club-gold-300 bg-club-gold-50 p-3 text-sm text-gray-900 dark:border-club-gold-700 dark:bg-club-gold-950/30 dark:text-gray-100">
        {`${item.minute}' Substitution ${item.direction} · ${substitutionPlayerLabel(item.counterpartPlayerId)} ${item.direction === 'on' ? 'off' : 'on'}`}
      </div>
    );
    const current = substitutionSlots.get(eventSlot);
    substitutionSlots.set(eventSlot, current ? <>{current}{substitution}</> : substitution);
  }
  const passAccuracy = fixture.passes === null || fixture.successfulPasses === null
    ? 'Not available'
    : fixture.passes === 0 ? 'Not available' : `${Math.round((fixture.successfulPasses / fixture.passes) * 100)}%`;
  const metrics = [
    ['minutes', 'Minutes', available(fixture.minutes)],
    ['goals', 'Goals', available(fixture.goals)],
    ['assists', 'Assists', available(fixture.assists)],
    ['xg', 'xG', available(fixture.xg, 2)],
    ['xa', 'xA', available(fixture.xgAssist, 2)],
    ['shots', 'Shots', available(fixture.shots)],
    ['pass-accuracy', 'Pass accuracy', passAccuracy],
    ['duels-won', 'Duels won', available(fixture.duelsWon)],
  ];

  const selectView = (next: 'moments' | 'all') => {
    setView(next);
    setSelectedEventId(null);
  };

  useEffect(() => {
    if (detailIdentityRef.current === detailIdentity) return;
    detailIdentityRef.current = detailIdentity;
    setView('moments');
    setSelectedEventId(firstMomentId);
  }, [detailIdentity, firstMomentId]);

  useEffect(() => {
    if (view !== 'moments' || firstMomentId === null || selectedMomentIsValid) return;
    setSelectedEventId(firstMomentId);
  }, [firstMomentId, selectedMomentIsValid, view]);

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, current: 'moments' | 'all') => {
    let next: 'moments' | 'all' | null = null;
    if (event.key === 'Home') next = 'moments';
    else if (event.key === 'End') next = 'all';
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') next = current === 'moments' ? 'all' : 'moments';
    if (!next) return;
    event.preventDefault();
    selectView(next);
    (next === 'moments' ? momentsTabRef : allTabRef).current?.focus();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showHeading && (
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-900">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
        </header>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <section aria-label="Match summary" className="rounded-xl bg-club-navy-950 p-5 text-white shadow-sm">
          <p className="text-sm font-semibold text-club-gold-300">
            {fixture.competitionName || 'Competition not available'}{fixture.seasonName ? ` · ${fixture.seasonName}` : ''}
          </p>
          <p className="mt-1 text-sm text-gray-300">{formatDate(fixture.dateUtc)} · {detail.match.status}</p>
          <p className="mt-4 text-center text-lg font-bold sm:text-xl">
            {homeTeamName || 'Home team'} {homeScore === null ? 'Not available' : homeScore} - {awayScore === null ? 'Not available' : awayScore} {awayTeamName || 'Away team'}
          </p>
          {detail.matchCacheState === 'unavailable' ? (
            <p className="mt-3 text-center text-sm text-amber-200">Detailed match information is unavailable.</p>
          ) : detail.matchSynchronizedAt ? (
            <p className="mt-3 text-center text-xs text-gray-300">Match detail synchronized {formatDate(detail.matchSynchronizedAt)}</p>
          ) : null}
        </section>

        <section aria-labelledby="player-match-summary" className="mt-5 rounded-xl border-2 border-club-gold-400 bg-white p-5 dark:border-club-gold-600 dark:bg-gray-900">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-club-gold-700 dark:text-club-gold-300">Player focus</p>
              <h2 id="player-match-summary" className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">{detail.player.name}</h2>
              {didNotPlay ? (
                <p className="mt-1 font-semibold text-gray-700 dark:text-gray-300">Did not play</p>
              ) : (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  {participation} · {fixture.position || 'Position not available'} · {fixture.minutes === null ? 'Minutes not available' : `${fixture.minutes} minutes`}
                </p>
              )}
              {targetSubstitutions.map((substitution, index) => (
                <p key={`${substitution.minute}:${substitution.direction}:${index}`} className="mt-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {substitution.direction === 'on' ? 'On' : 'Off'} {substitution.minute}' for {substitutionPlayerLabel(substitution.counterpartPlayerId)}
                </p>
              ))}
            </div>
            {fixture.participationStatus === 'played' && fixture.statsStatus !== 'available' && (
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">Statistics unavailable</span>
            )}
          </div>
          {didNotPlay ? (
            <p className="mt-4 rounded-lg bg-gray-100 p-3 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              {detail.player.name} did not participate in this fixture.
            </p>
          ) : (
            <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {metrics.map(([key, label, value]) => (
                <div key={key} data-testid={`player-metric-${key}`} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="mt-1 font-bold text-gray-900 dark:text-gray-100">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        <section aria-labelledby="player-match-lineups" className="mt-5 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
          <h2 id="player-match-lineups" className="font-bold text-gray-900 dark:text-gray-100">Lineups</h2>
          {detail.lineupStatus === 'unavailable' ? (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Lineup data is unavailable.</p>
          ) : (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {detail.lineups.map(team => (
                <div key={team.teamId} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{teamName(team.teamId)}{team.formation ? ` · ${team.formation}` : ''}</h3>
                  {team.lineup.length ? <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">{team.lineup.map(player => {
                    const name = resolvedPlayerName(player.playerId);
                    return <li key={player.playerId}>{lineupPlayerLabel(player)}{name && player.shirtNumber ? ` · #${player.shirtNumber}` : ''}{player.position ? ` · ${player.position}` : ''}</li>;
                  })}</ul> : <p className="mt-2 text-sm text-gray-500">No starters supplied.</p>}
                  {team.bench.length ? <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">{team.bench.map(player => {
                    const name = resolvedPlayerName(player.playerId);
                    return <li key={player.playerId}>{lineupPlayerLabel(player)}{name && player.shirtNumber ? ` · #${player.shirtNumber}` : ''}{player.position ? ` · ${player.position}` : ''}</li>;
                  })}</ul> : null}
                </div>
              ))}
            </div>
          )}
          <h3 className="mt-5 font-semibold text-gray-900 dark:text-gray-100">Substitutions</h3>
          {detail.substitutionStatus === 'unavailable' ? (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Substitution data is unavailable.</p>
          ) : detail.substitutions.length ? (
            <ul className="mt-2 space-y-1 text-sm text-gray-700 dark:text-gray-300">{detail.substitutions.map((substitution, index) => (
              <li key={`${substitution.teamId}:${substitution.minute}:${index}`}>{substitution.minute}' {teamName(substitution.teamId)}: {substitutionPlayerLabel(substitution.playerInId)} for {substitutionPlayerLabel(substitution.playerOutId)}</li>
            ))}</ul>
          ) : <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">No substitutions recorded.</p>}
        </section>

        <section className="mt-5" aria-label="Match events">
          <div role="tablist" aria-label="Event view" className="grid grid-cols-2 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
            <button
              ref={momentsTabRef}
              id={momentsTabId}
              type="button"
              role="tab"
              tabIndex={view === 'moments' ? 0 : -1}
              aria-selected={view === 'moments'}
              aria-controls={panelId}
              onClick={() => selectView('moments')}
              onKeyDown={event => handleTabKeyDown(event, 'moments')}
              className={`min-h-11 rounded-md px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${view === 'moments' ? 'bg-white text-club-navy-950 shadow-sm dark:bg-gray-700 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
            >
              Player moments
            </button>
            <button
              ref={allTabRef}
              id={allTabId}
              type="button"
              role="tab"
              tabIndex={view === 'all' ? 0 : -1}
              aria-selected={view === 'all'}
              aria-controls={panelId}
              onClick={() => selectView('all')}
              onKeyDown={event => handleTabKeyDown(event, 'all')}
              className={`min-h-11 rounded-md px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 ${view === 'all' ? 'bg-white text-club-navy-950 shadow-sm dark:bg-gray-700 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}
            >
              All events
            </button>
          </div>

          <div id={panelId} role="tabpanel" aria-labelledby={view === 'moments' ? momentsTabId : allTabId} className="mt-4">
            {detail.eventStatus === 'unavailable' ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
                <p>{detail.eventAvailability?.message || 'Match events are unavailable.'}</p>
                {detail.eventAvailability?.retryable && onRetry && (
                  <button type="button" onClick={onRetry} className="mt-4 min-h-11 rounded-md border border-gray-300 px-4 font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-700">Retry events</button>
                )}
              </div>
            ) : view === 'all' && allEventsAnalysis ? (
              allEventsAnalysis
            ) : view === 'moments' && momentItems.length > 0 ? (
              <EventTimeline
                events={timelineEvents}
                matchId={detail.match.matchId}
                canWatchVideo={false}
                requestEventVideo={unavailableVideoRequest}
                homeTeamId={detail.match.homeTeamId}
                awayTeamId={detail.match.awayTeamId}
                homeTeamName={homeTeamName || 'Home team'}
                awayTeamName={awayTeamName || 'Away team'}
                selectedEventId={selectedEventId}
                onSelectEvent={setSelectedEventId}
                emphasizedEventIds={new Set(timelineEvents.map(event => event.id))}
                eventRelationshipLabels={relationshipLabels}
                interleavedContent={substitutionSlots}
              />
            ) : timelineEvents.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
                {view === 'moments' ? 'No key moments for this fixture.' : 'No match events available.'}
              </p>
            ) : (
              <EventTimeline
                events={timelineEvents}
                matchId={detail.match.matchId}
                canWatchVideo={false}
                requestEventVideo={unavailableVideoRequest}
                homeTeamId={detail.match.homeTeamId}
                awayTeamId={detail.match.awayTeamId}
                homeTeamName={homeTeamName || 'Home team'}
                awayTeamName={awayTeamName || 'Away team'}
                selectedEventId={selectedEventId}
                onSelectEvent={setSelectedEventId}
                emphasizedEventIds={view === 'all' ? emphasizedEventIds : new Set(timelineEvents.map(event => event.id))}
                eventRelationshipLabels={relationshipLabels}
              />
            )}
          </div>
        </section>
      </div>

      <footer className="sticky bottom-0 z-10 flex min-h-16 items-center justify-between gap-4 border-t border-gray-200 bg-white px-5 py-3 dark:border-gray-700 dark:bg-gray-900">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{momentItems.length} key {momentItems.length === 1 ? 'moment' : 'moments'}</p>
        {footerAction}
      </footer>
    </div>
  );
};

export default WyscoutPlayerMatchContent;
